/**
 * Gestão de contatos pelo admin — C-75.
 *
 * Toda gravação invalida a raiz `['contatos']`: o modal de emergência do Header,
 * a sub-aba de suporte técnico, o e-mail do suporte do app (que também refaz o
 * cache dele no navegador, ver `useEmailSuporteApp`) e a própria lista de gestão
 * passam a mostrar o dado novo sem recarregar a página.
 *
 * Regra #2: tudo via datasource `contatos`. O gate real é a RLS (só admin grava);
 * `habilitado` só evita que outro perfil faça a consulta de gestão à toa.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataSources, type EdicaoContato, type NovoContato } from '@/services/datasources';

const RAIZ = ['contatos'] as const;

export function useContatosGestao(habilitado: boolean) {
  const { contatos } = useDataSources();
  return useQuery({
    queryKey: [...RAIZ, 'gestao'],
    enabled: habilitado,
    queryFn: () => contatos.listParaGestao(),
  });
}

/** GUPM-202 — usuários ativos do app, para o vínculo no formulário (só admin pede). */
export function useUsuariosDoApp(habilitado: boolean) {
  const { contatos } = useDataSources();
  return useQuery({
    queryKey: [...RAIZ, 'usuarios'],
    enabled: habilitado,
    staleTime: 5 * 60 * 1000,
    queryFn: () => contatos.listUsuariosDoApp(),
  });
}

export type GravacaoContato = { id: string; dados: EdicaoContato } | { id: null; dados: NovoContato };

export function useSalvarContato() {
  const { contatos } = useDataSources();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (g: GravacaoContato) =>
      g.id === null ? contatos.criar(g.dados) : contatos.atualizar(g.id, g.dados),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...RAIZ] }),
  });
}

export function useDefinirContatoAtivo() {
  const { contatos } = useDataSources();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) => contatos.definirAtivo(id, ativo),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...RAIZ] }),
  });
}
